import React from 'react';
import {useGuestSchema} from './GuestSchemaContext';
import {getDocumentType} from '../../store/selectors/getDocumentType';
import {getImageDetails} from '../../store/selectors/getImageDetails';
import {useIVStore} from '../../store/useIVStore';
import {IV_DOCUMENT_IMAGE_TYPES} from '@chekin/core';

function getIVDocTypes(isTwoSidesDocument: boolean, biomatch?: boolean) {
  const docTypes = isTwoSidesDocument
    ? [IV_DOCUMENT_IMAGE_TYPES.front, IV_DOCUMENT_IMAGE_TYPES.back]
    : [IV_DOCUMENT_IMAGE_TYPES.front];
  if (biomatch) {
    docTypes.push(IV_DOCUMENT_IMAGE_TYPES.selfie);
  }
  return docTypes;
}

function useIVComputedDetails() {
  const imagesDetails = useIVStore(getImageDetails);
  const documentType = useIVStore(getDocumentType);
  const declaredIvBypassAge = useIVStore(state => state.declaredIvBypassAge);
  const {
    isGuestSchemaLoaded: isDataLoaded,
    identityVerificationDetails: {
      enabled: isIVEnabled,
      biomatch,
      required,
      ocr,
      isIvExempted,
      isGuestRegisterBlocked,
      underageAllowedAge,
    },
  } = useGuestSchema();

  const isIVDocumentPhotosUploaded = React.useMemo(() => {
    if (!isIVEnabled || !documentType?.value) return true;
    const docTypes = getIVDocTypes(Boolean(documentType.two_sides));

    return docTypes.every(docType => imagesDetails[docType]?.uploaded);
  }, [isIVEnabled, imagesDetails, documentType]);

  const isIVCompleted = React.useMemo(() => {
    if (declaredIvBypassAge != null) return true;
    if (!isIVEnabled && !documentType?.value) return true;
    const docTypes = getIVDocTypes(Boolean(documentType?.two_sides), biomatch);
    return docTypes.every(docType => imagesDetails[docType]?.uploaded);
  }, [biomatch, declaredIvBypassAge, isIVEnabled, imagesDetails, documentType]);

  return {
    isIVDocumentPhotosUploaded,
    isIVCompleted,
    isDataLoaded,
    isIVEnabled,
    biomatch,
    required,
    ocr,
    isIvExempted,
    isGuestRegisterBlocked,
    underageAllowedAge,
    declaredIvBypassAge,
    isUnderageIvBypassDeclared: declaredIvBypassAge != null,
  };
}

export {useIVComputedDetails, getIVDocTypes};
